import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, lastValueFrom } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { AppInsightsService } from '../services/app-insights.service';
import { ConfigService } from '../services/config.service';
import { Result } from '@shared/models';
import * as Models from '@shared/models';
import * as Entities from '@shared/entities';
import { Enums } from 'app/app.enums';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly config = inject(ConfigService);
  private readonly appInsights = inject(AppInsightsService);

  private readonly enums = Enums;

  // Private writable signal for auth state
  private readonly _authData = signal<Models.AuthData | null>(this.loadStoredAuthData());

  // Public readonly signal - preferred API for new code
  readonly authData = this._authData.asReadonly();

  // Computed signals for common auth checks
  readonly currentUser = computed(() => this._authData()?.user ?? null);
  readonly isAuthenticated = computed(() => !!this._authData()?.accessToken);

  /**
   * @deprecated Use `authData()` signal instead. This observable is provided
   * for backwards compatibility during migration and will be removed in a future version.
   */
  readonly authData$: Observable<Models.AuthData | null> = toObservable(this._authData);

  // Service URI computed from config signal
  private readonly serviceUri = computed(() => this.config.config()?.apiUri ?? '');

  constructor() {
    // Effect to log config changes (replacing the subscription)
    effect(() => {
      const config = this.config.config();
      if (!config) {
        console.debug('Config is null');
      }
    });
    this.syncAuthenticatedUserContext(this._authData());
  }

  private loadStoredAuthData(): Models.AuthData | null {
    const storedAuthData = localStorage.getItem('authData');
    return storedAuthData ? JSON.parse(storedAuthData) : null;
  }

  changePassword(oldPassword: string, newPassword: string, confirmPassword: string): Promise<unknown> {
    const changePasswordModel: Models.ChangePassword = {
      oldPassword: oldPassword,
      password: newPassword,
      confirmPassword: confirmPassword,
    };

    return this.http
      .post(this.serviceUri() + '/account/changepassword', JSON.stringify(changePasswordModel), {})
      .toPromise();
  }

  resetPassword(userName: string, newPassword: string, confirmPassword: string): Promise<unknown> {
    const resetPasswordModel: Models.ResetPassword = {
      username: userName,
      password: newPassword,
      confirmPassword: confirmPassword,
    };

    return this.http
      .post(this.serviceUri() + '/account/resetpassword', JSON.stringify(resetPasswordModel), {})
      .toPromise();
  }

  setAuthData(authData: Models.AuthData): void {
    console.debug('setAuthData', authData);
    localStorage.setItem('authData', JSON.stringify(authData));
    this._authData.set(authData);
    this.syncAuthenticatedUserContext(authData);
  }

  async samlLogin(client: string, saml: string): Promise<Result<string | null>> {
    const samlLoginInfo = { client: client, samlAssertion: saml };
    const headers = new HttpHeaders().append('Content-Type', 'application/json');

    try {
      const result = await lastValueFrom(
        this.http.post<Models.AuthData>(this.serviceUri() + '/auth/saml', samlLoginInfo, { headers: headers })
      );
      // store auth token in local storage so we can make the /auth/user call
      this.setAuthData(result);

      const user = await lastValueFrom(this.http.get<Entities.User>(this.serviceUri() + '/auth/user'));
      result.user = user;
      this.setAuthData(result);
      this.appInsights.trackEvent('Usage.SamlLoginSucceeded', {
        props: {
          Method: 'Saml',
          Client: client,
        },
      });

      return Result.success('Success!');
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        console.debug('samlLogin: ', error);
        this.trackLoginFailure('Saml', error.status, client);
        if (error.status === 400) {
          console.error('Invalid SAML token.');
          return Result.error('Invalid SAML token.');
        }
        if (error.status === 403) {
          console.error('User is locked out.');
        }
        if (error.status === 404) {
          console.error('User is not found.');
        }
        if (error.status === 500) {
          console.error('Internal server error.');
        }
      } else {
        this.trackLoginFailure('Saml');
      }
      return Result.error('Unknown error.');
    }
  }

  async samlLoginComplete(accessToken: string): Promise<Result<string | null>> {
    try {
      const authData: Models.AuthData = {
        accessToken: accessToken,
        tokenType: 'Bearer',
        expires: '',
        user: null,
      };
      this.setAuthData(authData);
      const user = await lastValueFrom(this.http.get<Entities.User>(this.serviceUri() + '/auth/user'));
      authData.user = user;
      this.setAuthData(authData);
      this.appInsights.trackEvent('Usage.SamlLoginSucceeded', {
        props: {
          Method: 'SamlComplete',
        },
      });
      return Result.success('Success!');
    } catch (error) {
      console.debug('samlLoginComplete: ', error);
      this.trackLoginFailure('SamlComplete');
      return Result.error('saml login complete error');
    }
  }

  async login(userName: string, password: string): Promise<Result<string | null>> {
    const loginInfo = { username: userName, password: password };
    const headers = new HttpHeaders().append('Content-Type', 'application/json');

    try {
      const result = await lastValueFrom(
        this.http.post<Models.AuthData>(this.serviceUri() + '/auth/token', loginInfo, { headers: headers })
      );
      // store auth token in local storage so we can make the /auth/user call
      this.setAuthData(result);

      const user = await lastValueFrom(this.http.get<Entities.User>(this.serviceUri() + '/auth/user'));
      result.user = user;
      this.setAuthData(result);
      this.appInsights.trackEvent('Usage.LoginSucceeded', {
        props: {
          Method: 'Password',
        },
      });

      return Result.success('Success!');
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        console.debug('login: ', error);
        this.trackLoginFailure('Password', error.status);
        if (error.status === 400) {
          console.error('Invalid username or password.');
          return Result.error('Invalid username or password.');
        }
        if (error.status === 403) {
          console.error('User is locked out.');
        }
        if (error.status === 404) {
          console.error('User is not found.');
        }
        if (error.status === 500) {
          console.error('Internal server error.');
        }
      } else {
        this.trackLoginFailure('Password');
      }
      return Result.error('Unknown error.');
    }
  }

  logOut(): void {
    void this.appInsights.trackEvent('Usage.Logout');
    // remove user from local storage to log user out
    localStorage.removeItem('authData');
    this._authData.set(null);
    this.appInsights.clearAuthenticatedUser();
    void this.router.navigateByUrl('/login');
  }

  /**
   * @deprecated Use `currentUser()` signal instead
   */
  getCurrentUser(): Entities.User | null | undefined {
    return this._authData()?.user ?? null;
  }

  /**
   * @deprecated Use `authData()` signal instead
   */
  getAuthData(): Models.AuthData | null {
    return this._authData();
  }

  isInRole(roleName: string): boolean {
    if (roleName === '*') {
      return true;
    }
    return this.getRoles()?.filter((r) => r === roleName).length > 0;
  }

  isInProgram(programId: number): boolean {
    const ids = this.getPrograms().map((p) => p.programId);
    const exists = ids.indexOf(programId) > -1;
    return exists;
  }

  private getRoles(): string[] {
    const tokenDecoded = this.GetTokenDecoded();
    if (!tokenDecoded) {
      return [];
    }
    const rolesValue = tokenDecoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    // if roles is not an array, make it an array
    let roles: string[];
    if (!Array.isArray(rolesValue)) {
      roles = rolesValue ? [String(rolesValue)] : [];
    } else {
      roles = rolesValue as string[];
    }
    return roles;
  }

  private getPrograms(): Entities.RetailerProgram[] {
    const programs = this._authData()?.user?.programs;
    return programs ?? [];
  }

  GetTokenDecoded(): Record<string, unknown> | null {
    const authValue = this._authData();
    if (!authValue?.accessToken) {
      this.logOut();
      return null;
    }
    const tokenSections = authValue.accessToken.split('.');
    if (tokenSections.length < 2) {
      this.logOut();
      return null;
    }
    const tokenEncoded = tokenSections[1];
    return JSON.parse(atob(tokenEncoded ?? ''));
  }

  isShiftAdmin(): boolean {
    return this.isInRole(this.enums.role.shiftAdmin);
  }

  isShiftAnalyst(): boolean {
    return this.isInRole(this.enums.role.shiftAnalyst);
  }

  isConciergeManager(): boolean {
    return this.isInRole(this.enums.role.conciergeManager);
  }

  isConciergeAgent(): boolean {
    return this.isInRole(this.enums.role.conciergeAgent);
  }

  isRetailerUser(): boolean {
    return this.isInRole(this.enums.role.retailerManager) || this.isInRole(this.enums.role.retailerRepresentative);
  }

  isRetailerManager(): boolean {
    return this.isInRole(this.enums.role.retailerManager);
  }

  isRetailerRepresentative(): boolean {
    return this.isInRole(this.enums.role.retailerRepresentative);
  }

  isCorporate(): boolean {
    return this.isInRole(this.enums.role.corporate);
  }

  isInboundRetailer(): boolean {
    return this.isInProgram(this.enums.program.inboundService);
  }

  isRecallConciergeRetailer(): boolean {
    return this.isInProgram(this.enums.program.recallConcierge);
  }

  private syncAuthenticatedUserContext(authData: Models.AuthData | null): void {
    if (authData?.user) {
      this.appInsights.setAuthenticatedUser(authData.user);
    } else {
      this.appInsights.clearAuthenticatedUser();
    }
  }

  private trackLoginFailure(method: string, status?: number, client?: string): void {
    this.appInsights.trackEvent(method === 'Password' ? 'Usage.LoginFailed' : 'Usage.SamlLoginFailed', {
      props: {
        Method: method,
        StatusCode: status === undefined ? '' : String(status),
        Client: client ?? '',
      },
    });
  }

  checkIsNewInstance(): Observable<Result<Entities.CheckIsNewInstanceResponse>> {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.get<Result<Entities.CheckIsNewInstanceResponse>>(
      this.serviceUri() + '/users/check-is-new-instance',
      { headers: headers }
    );
  }

  createFirstAdmin(request: Entities.CreateFirstAdminUserRequest): Observable<Entities.CreateFirstAdminUserResponse> {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.post<Entities.CreateFirstAdminUserResponse>(
      this.serviceUri() + '/users/create-first-admin',
      request,
      { headers: headers }
    );
  }
}
